import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getSocket } from '../../socket';
import {
    shareReceived,
    shareRevoked,
    shareRequestDecided,
    noteUnseenShare,
} from '../../redux/appointmentShareSlice';

// VISIT SHARING, LIVE (visit sharing v2, owner 24 Sep 2026) — the doctor's
// half. A patient answers in their own app, at their own time; the doctor
// should learn of it wherever they are in the app, not on their next visit to
// "Shared with me". Mounted once in AppLayout, like MocLinkSocketBridge.
//
// The doctor's room (`doctor_<doctorId>`) is joined by the backend on the
// socket HANDSHAKE — src/socket.js sends `doctorId` in the query for a doctor —
// so there is nothing to emit here; the bridge only listens.
//
// Events (backend socket/visitShareEvents.js):
//   visit-share:received { share, patientName, appointmentDate, hospitalName,
//                          viaRequest, requestId }
//       → prepend the row and count it for the sidebar badge unless the
//         doctor is already looking at the page — for EVERY receive. Toast
//         only when the patient shared on their own (viaRequest false): a
//         grant of 3 visits against a doctor's ask emits 3 receives AND one
//         decided, and the doctor gets ONE toast for it (owner, 24 Sep 2026);
//   visit-share:decided  { requestId, status, count, patientName }
//       → one toast ("Kiran shared 3 visits" / "declined"), and flip the
//         "Asked · waiting" chip;
//   visit-share:revoked  { shareId }
//       → drop the row (the snapshot would 403 anyway).
export default function VisitShareSocketBridge() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const tRef = useRef(t);
    useEffect(() => { tRef.current = t; }, [t]);
    const location = useLocation();
    const pathRef = useRef(location.pathname);
    useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);

    useEffect(() => {
        const role = (localStorage.getItem('Role') || '').toLowerCase();
        // Shares and asks are between a patient and a DOCTOR; nobody else's
        // socket is in the doctor room.
        if (role !== 'doctor') return undefined;
        let alive = true;
        let retryTimer = null;
        let attached = null;

        const onReceived = (p) => {
            if (!p || !p.share || !p.share.shareId) return;
            const tt = tRef.current;
            const patientName = p.patientName || p.share.patientName || tt('shareRequests.the_patient_cap', 'A patient');
            const share = {
                ...p.share,
                patientName,
                appointmentDate: p.share.appointmentDate || p.appointmentDate || null,
                hospitalName: p.share.hospitalName || p.hospitalName || null,
            };
            dispatch(shareReceived(share));
            const viaRequest = p.viaRequest === true || p.share.viaRequest === true;
            const hospital = share.hospitalName;
            if (!viaRequest) toast.info(hospital
                ? tt('shareRequests.received_toast', { name: patientName, hospital, defaultValue: `${patientName} shared a visit from ${hospital} with you` })
                : tt('shareRequests.received_toast_nohospital', { name: patientName, defaultValue: `${patientName} shared a visit with you` }));
            if (pathRef.current !== '/shared-visits') dispatch(noteUnseenShare());
        };

        const onDecided = (p) => {
            if (!p || !p.requestId) return;
            const tt = tRef.current;
            const name = p.patientName || tt('shareRequests.the_patient_cap', 'A patient');
            if (p.status === 'granted') {
                const count = Number(p.count) || 0;
                toast.success(tt('shareRequests.decided_granted', { name, count, defaultValue: `${name} shared ${count} visits` }));
            } else if (p.status === 'rejected') {
                toast.info(tt('shareRequests.decided_rejected', { name, defaultValue: `${name} declined` }));
            }
            dispatch(shareRequestDecided(p));
        };

        const onRevoked = (p) => {
            if (!p || !p.shareId) return;
            dispatch(shareRevoked(p.shareId));
        };

        // Same attach-with-retry as MocLinkSocketBridge: the shared socket may
        // not exist yet at mount. Never disconnect it on cleanup — others use it.
        const attach = () => {
            if (!alive) return;
            const socket = getSocket && getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attached = socket;
            socket.on('visit-share:received', onReceived);
            socket.on('visit-share:decided', onDecided);
            socket.on('visit-share:revoked', onRevoked);
            if (!socket.connected) socket.connect();
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attached) {
                attached.off('visit-share:received', onReceived);
                attached.off('visit-share:decided', onDecided);
                attached.off('visit-share:revoked', onRevoked);
            }
        };
    }, [dispatch]);

    return null;
}
